import { BasicFormState, ValidationResults } from 'hooks/useForm';
import TextButton from 'components/inputs/TextButton';
import { AssignmentField, ExcludableKeyListRow } from './types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faWarning } from '@fortawesome/free-solid-svg-icons';
import { ReactNode, useCallback, useMemo } from 'react';
import {
  AssignmentTypeEnum,
  CaseTypeEnum,
  CommuteOptionDto,
  CompanyModel,
  GlobalSettingEnum,
} from 'api/model';
import {
  Horizontal,
  InFormContainer,
  PaddedSpan,
  WarningDiv,
  WarningSpan,
} from './style';
import LabelWrap from 'components/inputs/LabelWrap';
import { openGoogleMapsDirectionsArray } from 'utils/address-helper';
import useGetAssignmentAddresses from './useGetAssignmentAddresses';
import TooltipInfo from 'components/TooltipInfo';
import BasicEstimatedDurationPicker from '../CaseAssignments/BasicEstimatedDurationPicker';
import usePersonalTransportCalculatedTimes from './usePersonalTransportTimes';
import { formatDateTime, toInputDateTimeString } from 'utils/date-helpers';
import Select from 'components/inputs/Select';
import useAssignmentTypeOptions from 'hooks/useAssignmentTypeOptions';
import useTranslations from 'contexts/basicData/useTranslations';
import useMe from 'contexts/authentication/useMe';
import useGlobalSettings from 'hooks/useGlobalSettings';
import useExpenseTypeOptions from './useExpenseTypeOptions';
import MultiSelect from 'components/inputs/MultiSelect';
import Checkbox from 'components/inputs/Checkbox';
import AutoSizedTextArea from 'components/inputs/AutoSizedTextArea';
import styled from 'styled-components';
import Input from 'components/inputs/Input';
import CommuteTimePicker from './CommuteTimePicker';
import { replaceAllTransportationModeNames } from './googleMapsRouteHelper';
import {
  getExpectedExpensesBasedOnSelfTransportation,
  travelsBetweenDifferentCounties,
} from './useCaseAssignmentForms';
import { googleMapsRoutingBaseURL } from 'constants/AppConstants';

const ExtraInfoInput = styled(AutoSizedTextArea)`
  resize: vertical;
  width: 395px;
  max-width: 395px;
`;

const HorizontalWithGaps = styled(Horizontal)`
  padding: 2px 0px;
  gap: 10px;
`;

const toTransportRowId = 'addPersonalTransportToAssignment';
const fromTransportRowId = 'addPersonalTransportFromAssignment';

type ReturnProps = {
  toAssignment: ExcludableKeyListRow;
  fromAssignment: ExcludableKeyListRow;
};

type Props = {
  assignmentId: number;
  state: BasicFormState<AssignmentField>;
  currentCaseType: CaseTypeEnum;
  validation: ValidationResults<AssignmentField> | undefined;

  mkAddressInputFn: (
    name: AssignmentField,
    address: AssignmentField,
    city: AssignmentField,
    zip: AssignmentField,
    county: AssignmentField,
    label: ReactNode,
    filter?: (c: CompanyModel) => boolean,
    disabled?: boolean,
    warning?: string,
    extra?: React.ReactNode
  ) => ExcludableKeyListRow;

  onChange: (
    assignmentId: number,
    field: AssignmentField,
    value: string
  ) => void;
};

const useAddPersonalTransportAssignmentRow = ({
  assignmentId,
  state,
  currentCaseType,
  validation,

  mkAddressInputFn,
  onChange,
}: Props): ReturnProps => {
  const loggedInUser = useMe();
  const expenseTypeOptions = useExpenseTypeOptions();

  const { parsedData: globalSettings } = useGlobalSettings();
  const hasAccessToInEditStopOvers =
    globalSettings[GlobalSettingEnum.UsersWithAccessToInEditStopOvers]?.[
      loggedInUser?.userID ?? -1
    ] === true;

  const { assignmentTypesShort } = useTranslations();

  const assignmentTypeOptions = useAssignmentTypeOptions({
    onlyPersonalTransport: true, // hardcoded to placeholder as we only want those types
  });
  const { personalTransportToAssignmentStart } =
    useGetAssignmentAddresses(state);

  const {
    personalTransportToAssignmentStartTime,
    personalTransportToAssignmentEndTime,
  } = usePersonalTransportCalculatedTimes(state);

  const handleAddPersonalTransportToAssignment = useCallback(() => {
    onChange(
      assignmentId,
      AssignmentField.addingPersonalTransportationToAssignment,
      String('true')
    );
  }, [assignmentId, onChange]);

  const handleAddPersonalTransportFromAssignment = useCallback(() => {
    onChange(
      assignmentId,
      AssignmentField.addingPersonalTransportationFromAssignment,
      String('true')
    );
  }, [assignmentId, onChange]);

  const createErrorLabel = useCallback(
    (field: AssignmentField) => {
      return (
        (validation?.[field]?.error || validation?.[field]?.warning) && (
          <TooltipInfo
            hoverable
            icon={faWarning}
            error={!!validation?.[field]?.error}
            warning={!!validation?.[field]?.warning}
            info={
              <div style={{ minWidth: 300, textAlign: 'left' }}>
                {validation[field]?.error}
                <br />
                {validation[field]?.warning}
              </div>
            }
          />
        )
      );
    },
    [validation] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const joinAddress = useCallback(
    (parts: Array<string | undefined | null>) =>
      parts.filter(Boolean).join(', '),
    []
  );

  const handleCommuteSelect = useCallback(
    (
      option: CommuteOptionDto,
      isToAssignment: boolean,
      fromAddress: string | undefined,
      toAddress: string | undefined
    ) => {
      const durationMs = option.durationSeconds * 1000;

      // For "to assignment": use departure time (when to start the journey)
      // For "from assignment": use departure time (when to start the return journey)
      let startTime = option.departureTimeEpoch
        ? toInputDateTimeString(new Date(option.departureTimeEpoch * 1000))
        : null;

      // if we have an arrival time epoch, and it's a "to assignment", we can calculate the start time based on that and the duration
      if (isToAssignment && option.arrivalTimeEpoch) {
        const arrivalTime = new Date(option.arrivalTimeEpoch * 1000);
        const departureTime = new Date(arrivalTime.getTime() - durationMs);
        startTime = toInputDateTimeString(departureTime);
      }

      const mapsUrl = googleMapsRoutingBaseURL
        .replace('{origin}', encodeURIComponent(fromAddress || ''))
        .replace('{destination}', encodeURIComponent(toAddress || ''));
      const ticketInfo = `Avresa: ${
        option.departureTimeEpoch
          ? formatDateTime(new Date(option.departureTimeEpoch * 1000))
          : '-'
      }\nAnkomst: ${
        option.arrivalTimeEpoch
          ? formatDateTime(new Date(option.arrivalTimeEpoch * 1000))
          : '-'
      }\n${replaceAllTransportationModeNames(
        option.description || ''
      )}\n\nLänk: ${mapsUrl}`;

      if (isToAssignment) {
        // Set estimated duration
        onChange(
          assignmentId,
          AssignmentField.personalTransportationToAssignmentEstimatedDuration,
          String(durationMs)
        );
        // Set departure time as the estimated start time for the personal transport
        if (startTime) {
          onChange(
            assignmentId,
            AssignmentField.personalTransportationToAssignmentEstimatedStartTime,
            startTime
          );
        }
        onChange(
          assignmentId,
          AssignmentField.personalTransportationToTicketInfo,
          ticketInfo
        );
        // Set estimated start time for the main assignment to be the arrival time of the personal transport
        if (option.arrivalTimeEpoch) {
          // Use arrivalTimeEpoch directly - it correctly includes transfer/waiting times between journey legs
          const arrivalTime = new Date(option.arrivalTimeEpoch * 1000);

          const nextDepartureTime = new Date(
            arrivalTime.getTime() + 1 * 60 * 1000
          ); // add 1 minute buffer

          onChange(
            assignmentId,
            AssignmentField.estimatedStartTime,
            toInputDateTimeString(nextDepartureTime)
          );
        }
      } else {
        // From assignment
        onChange(
          assignmentId,
          AssignmentField.personalTransportationFromAssignmentEstimatedDuration,
          String(durationMs)
        );
        if (startTime) {
          onChange(
            assignmentId,
            AssignmentField.personalTransportationFromAssignmentEstimatedStartTime,
            startTime
          );
        }
        onChange(
          assignmentId,
          AssignmentField.personalTransportationFromTicketInfo,
          ticketInfo
        );
      }
    },
    [assignmentId, onChange]
  );

  const result = useMemo(() => {
    const toAssignment: ExcludableKeyListRow =
      state[AssignmentField.addingPersonalTransportationToAssignment] === 'true'
        ? {
            key: <WarningSpan>Personlig transport</WarningSpan>,
            id: toTransportRowId,
            value: (
              <InFormContainer>
                <LabelWrap
                  label={
                    <>
                      <strong>Personlig transport till uppdraget</strong>
                      <TextButton
                        onClick={() =>
                          onChange(
                            assignmentId,
                            AssignmentField.addingPersonalTransportationToAssignment,
                            String('false')
                          )
                        }
                      >
                        <TooltipInfo
                          hoverable
                          info={
                            <div style={{ minWidth: 300, textAlign: 'left' }}>
                              Uppdraget kommer att delas upp med en personlig
                              transport. Detta går endast att göra när ingen FT
                              ännu är tilldelad. Om du ångrar dig och vill ta
                              bort transporten igen, klicka på knappen{' '}
                              <em>Ångra personlig transport</em>.
                            </div>
                          }
                        />
                        <FontAwesomeIcon icon={faMinus} /> Ångra personlig
                        transport
                      </TextButton>
                    </>
                  }
                >
                  <Horizontal>
                    {
                      mkAddressInputFn(
                        AssignmentField.personalTransportationToAssignmentName,
                        AssignmentField.personalTransportationToAssignmentAddress,
                        AssignmentField.personalTransportationToAssignmentCity,
                        AssignmentField.personalTransportationToAssignmentZip,
                        AssignmentField.personalTransportationToAssignmentCounty,
                        'Personlig transport till uppdraget',
                        undefined,
                        false,
                        undefined,
                        undefined
                      ).value
                    }

                    <TextButton
                      onClick={() => {
                        openGoogleMapsDirectionsArray(
                          personalTransportToAssignmentStart
                        );
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="Öppna i Google Maps"
                    >
                      <img
                        alt="Google maps icon"
                        height={15}
                        src="/images/googlemapsiconsmall.png"
                        width={15}
                      />
                    </TextButton>
                  </Horizontal>
                </LabelWrap>
                <LabelWrap label={<strong>Typ</strong>}>
                  <div
                    style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                  >
                    <Select
                      onChange={(eve) => {
                        const newAssignmentType = Number(
                          eve.currentTarget.value
                        ) as AssignmentTypeEnum;
                        onChange(
                          assignmentId,
                          AssignmentField.personalTransportationToAssignmentAssignmentTypeID,
                          eve.currentTarget.value
                        );

                        // Auto-add appropriate expense type for transport mode.
                        // Jämför den personliga transportens start-län med
                        // huvuduppdragets start-län.
                        const expectedExpense =
                          getExpectedExpensesBasedOnSelfTransportation(
                            newAssignmentType,
                            travelsBetweenDifferentCounties(
                              state[
                                AssignmentField
                                  .personalTransportationToAssignmentCounty
                              ],
                              state[AssignmentField.fromCounty]
                            )
                          );
                        if (expectedExpense) {
                          const currentExpenses = state[
                            AssignmentField
                              .personalTransportationToExpectedExpenses
                          ]
                            ? state[
                                AssignmentField
                                  .personalTransportationToExpectedExpenses
                              ].split(',')
                            : [];

                          if (
                            !currentExpenses.includes(
                              expectedExpense.toString()
                            )
                          ) {
                            const newExpenses = [
                              ...currentExpenses,
                              expectedExpense.toString(),
                            ].filter(Boolean);
                            onChange(
                              assignmentId,
                              AssignmentField.personalTransportationToExpectedExpenses,
                              newExpenses.join(',')
                            );
                          }
                        }
                      }}
                      small
                      value={
                        state[
                          AssignmentField
                            .personalTransportationToAssignmentAssignmentTypeID
                        ]
                      }
                    >
                      {assignmentTypeOptions.map((assignmentType) => (
                        <option key={assignmentType} value={assignmentType}>
                          {assignmentTypesShort[assignmentType]}
                        </option>
                      ))}
                    </Select>
                    {Number(
                      state[
                        AssignmentField
                          .personalTransportationToAssignmentAssignmentTypeID
                      ]
                    ) === AssignmentTypeEnum.GoByPublicTransport && (
                      <CommuteTimePicker
                        // from the personal transport start address to the main assignment start address

                        fromAddress={joinAddress([
                          state[
                            AssignmentField
                              .personalTransportationToAssignmentAddress
                          ],
                          state[
                            AssignmentField
                              .personalTransportationToAssignmentCity
                          ],
                          state[
                            AssignmentField
                              .personalTransportationToAssignmentZip
                          ],
                        ])}
                        toAddress={joinAddress([
                          state[AssignmentField.fromAddress],
                          state[AssignmentField.fromCity],
                          state[AssignmentField.fromZip],
                        ])}
                        estimatedStartTime={
                          state[AssignmentField.estimatedStartTime] ||
                          state[AssignmentField.bookedTo] // default to estimated start time of main assignment, fallback to bookedTo if not set
                        }
                        personalTransportToMain
                        onSelectOption={(option, fromAddress, toAddress) =>
                          handleCommuteSelect(
                            option,
                            true,
                            fromAddress,
                            toAddress
                          )
                        }
                      />
                    )}
                  </div>
                </LabelWrap>
                <HorizontalWithGaps>
                  <LabelWrap
                    errorLabel={createErrorLabel(
                      AssignmentField.personalTransportationToAssignmentEstimatedStartTime
                    )}
                    label={<strong>Estimerad starttid</strong>}
                  >
                    <Horizontal style={{ alignItems: 'center' }}>
                      <Input
                        error={
                          !!validation?.[
                            AssignmentField
                              .personalTransportationToAssignmentEstimatedStartTime
                          ]?.error
                        }
                        warning={
                          !!validation?.[
                            AssignmentField
                              .personalTransportationToAssignmentEstimatedStartTime
                          ]?.warning
                        }
                        onChange={(eve) => {
                          const personalTransportationToAssignmentEstimatedStartTime =
                            eve.currentTarget.value;

                          // Always commit the value, even if it (for now) ends after the
                          // main assignment's estimated start time. The user is often
                          // typing the time with the keyboard, and a half-typed value
                          // must not be rejected. The validator for this field shows an
                          // inline error next to the input and disables save instead.
                          onChange(
                            assignmentId,
                            AssignmentField.personalTransportationToAssignmentEstimatedStartTime,
                            personalTransportationToAssignmentEstimatedStartTime
                          );

                          // then, update the estimated duration with the time between the main assignment estimated start time and the personal transport estimated start time (if there is one)
                          if (
                            state[AssignmentField.estimatedStartTime] &&
                            personalTransportationToAssignmentEstimatedStartTime
                          ) {
                            const estimatedStartTimeOfMainAssignment = new Date(
                              state[AssignmentField.estimatedStartTime]
                            );
                            const personalTransportEstimatedStartTime =
                              new Date(
                                personalTransportationToAssignmentEstimatedStartTime
                              );

                            // calculate the difference in milliseconds
                            const estimatedDuration =
                              estimatedStartTimeOfMainAssignment.getTime() -
                              personalTransportEstimatedStartTime.getTime();
                            // if the estimated duration is positive, update the state

                            if (estimatedDuration > 0) {
                              onChange(
                                assignmentId,
                                AssignmentField.personalTransportationToAssignmentEstimatedDuration,
                                String(estimatedDuration)
                              );
                            }
                          }
                        }}
                        small
                        type="datetime-local"
                        value={
                          state[
                            AssignmentField
                              .personalTransportationToAssignmentEstimatedStartTime
                          ]
                        }
                      />
                    </Horizontal>
                  </LabelWrap>
                  <LabelWrap
                    errorLabel={createErrorLabel(
                      AssignmentField.personalTransportationToAssignmentEstimatedStartTime
                    )}
                    label={<strong>Estimerad tid</strong>}
                  >
                    <HorizontalWithGaps style={{ alignItems: 'center' }}>
                      <BasicEstimatedDurationPicker
                        error={
                          !!validation?.[
                            AssignmentField
                              .personalTransportationToAssignmentEstimatedStartTime
                          ]?.error
                        }
                        disabled={
                          state[
                            AssignmentField
                              .personalTransportationToAssignmentFixedAssignmentTimeInHours
                          ] !== ''
                        }
                        caseType={currentCaseType}
                        assignmentType={
                          Number(
                            state[
                              AssignmentField
                                .personalTransportationToAssignmentAssignmentTypeID
                            ]
                          ) as AssignmentTypeEnum
                        }
                        addresses={personalTransportToAssignmentStart}
                        value={
                          state[
                            AssignmentField
                              .personalTransportationToAssignmentEstimatedDuration
                          ]
                            ? Number(
                                state[
                                  AssignmentField
                                    .personalTransportationToAssignmentEstimatedDuration
                                ]
                              )
                            : undefined
                        }
                        hideMapsIcon
                        onChange={(value) => {
                          // first, update the state with the new value
                          onChange(
                            assignmentId,
                            AssignmentField.personalTransportationToAssignmentEstimatedDuration,
                            value.toString()
                          );
                        }}
                      />
                      {state[
                        AssignmentField
                          .personalTransportationToAssignmentFixedAssignmentTimeInHours
                      ] !== '' && (
                        <WarningDiv style={{ marginLeft: 5 }}>
                          Fast tid:{' '}
                          {
                            state[
                              AssignmentField
                                .personalTransportationToAssignmentFixedAssignmentTimeInHours
                            ]
                          }{' '}
                          h
                        </WarningDiv>
                      )}
                      {personalTransportToAssignmentEndTime && (
                        <PaddedSpan style={{ opacity: 0.4 }}>
                          Beräknad sluttid:
                          {formatDateTime(personalTransportToAssignmentEndTime)}
                        </PaddedSpan>
                      )}
                    </HorizontalWithGaps>
                  </LabelWrap>
                </HorizontalWithGaps>
                <HorizontalWithGaps>
                  <LabelWrap label={<strong>Förväntade utgifter</strong>}>
                    <MultiSelect
                      onChange={(v) => {
                        onChange(
                          assignmentId,
                          AssignmentField.personalTransportationToExpectedExpenses,
                          Array.from(v).join(',')
                        );
                      }}
                      options={expenseTypeOptions}
                      small
                      value={
                        new Set(
                          state[
                            AssignmentField
                              .personalTransportationToExpectedExpenses
                          ] === ''
                            ? []
                            : state[
                                AssignmentField
                                  .personalTransportationToExpectedExpenses
                              ].split(',')
                        )
                      }
                    />
                  </LabelWrap>
                </HorizontalWithGaps>
                <HorizontalWithGaps>
                  <LabelWrap
                    label={<strong>Fälttestaren bokar egen biljett</strong>}
                  >
                    <Checkbox
                      checked={
                        state[
                          AssignmentField
                            .personalTransportationToAssigneeShouldBookTheirOwnTicket
                        ] === 'true'
                      }
                      onChange={(eve) =>
                        onChange(
                          assignmentId,
                          AssignmentField.personalTransportationToAssigneeShouldBookTheirOwnTicket,
                          eve.currentTarget.checked ? 'true' : 'false'
                        )
                      }
                    />
                  </LabelWrap>
                </HorizontalWithGaps>
                <LabelWrap
                  errorLabel={createErrorLabel(
                    AssignmentField.personalTransportationToTicketInfo
                  )}
                  label={<strong>Biljett-info</strong>}
                >
                  <ExtraInfoInput
                    onChange={(eve) =>
                      onChange(
                        assignmentId,
                        AssignmentField.personalTransportationToTicketInfo,
                        eve.currentTarget.value
                      )
                    }
                    value={
                      state[AssignmentField.personalTransportationToTicketInfo]
                    }
                  />
                </LabelWrap>
              </InFormContainer>
            ),
          }
        : {
            key: 'Lägg till',
            id: toTransportRowId,
            exclude: () => !hasAccessToInEditStopOvers,
            value: (
              <TextButton onClick={handleAddPersonalTransportToAssignment}>
                <FontAwesomeIcon icon={faPlus} /> Personlig transport till
                uppdraget
              </TextButton>
            ),
          };

    const fromAssignment: ExcludableKeyListRow =
      state[AssignmentField.addingPersonalTransportationFromAssignment] ===
      'true'
        ? {
            key: <WarningSpan>Personlig transport</WarningSpan>,
            id: fromTransportRowId,
            value: (
              <InFormContainer>
                <LabelWrap
                  label={
                    <>
                      <strong>Personlig transport från uppdraget</strong>
                      <TextButton
                        onClick={() =>
                          onChange(
                            assignmentId,
                            AssignmentField.addingPersonalTransportationFromAssignment,
                            String('false')
                          )
                        }
                      >
                        <TooltipInfo
                          hoverable
                          info={
                            <div style={{ minWidth: 300, textAlign: 'left' }}>
                              Uppdraget kommer att delas upp med en personlig
                              transport. Detta går endast att göra när ingen FT
                              ännu är tilldelad. Om du ångrar dig och vill ta
                              bort transporten igen, klicka på knappen{' '}
                              <em>Ångra personlig transport</em>.
                            </div>
                          }
                        />
                        <FontAwesomeIcon icon={faMinus} /> Ångra personlig
                        transport
                      </TextButton>
                    </>
                  }
                >
                  <Horizontal>
                    {
                      mkAddressInputFn(
                        AssignmentField.personalTransportationFromAssignmentName,
                        AssignmentField.personalTransportationFromAssignmentAddress,
                        AssignmentField.personalTransportationFromAssignmentCity,
                        AssignmentField.personalTransportationFromAssignmentZip,
                        AssignmentField.personalTransportationFromAssignmentCounty,
                        'Personlig transport från uppdraget',
                        undefined,
                        false,
                        undefined,
                        undefined
                      ).value
                    }

                    <TextButton
                      onClick={() => {
                        openGoogleMapsDirectionsArray(
                          personalTransportToAssignmentStart
                        );
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="Öppna i Google Maps"
                    >
                      <img
                        alt="Google maps icon"
                        height={15}
                        src="/images/googlemapsiconsmall.png"
                        width={15}
                      />
                    </TextButton>
                  </Horizontal>
                </LabelWrap>
                <LabelWrap label={<strong>Typ</strong>}>
                  <div
                    style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                  >
                    <Select
                      onChange={(eve) => {
                        const newAssignmentType = Number(
                          eve.currentTarget.value
                        ) as AssignmentTypeEnum;
                        onChange(
                          assignmentId,
                          AssignmentField.personalTransportationFromAssignmentAssignmentTypeID,
                          eve.currentTarget.value
                        );

                        // Auto-add appropriate expense type for transport mode.
                        // Jämför den personliga transportens slut-län med
                        // huvuduppdragets slut-län.
                        const expectedExpense =
                          getExpectedExpensesBasedOnSelfTransportation(
                            newAssignmentType,
                            travelsBetweenDifferentCounties(
                              state[
                                AssignmentField
                                  .personalTransportationFromAssignmentCounty
                              ],
                              state[AssignmentField.toCounty]
                            )
                          );
                        if (expectedExpense) {
                          const currentExpenses = state[
                            AssignmentField
                              .personalTransportationFromExpectedExpenses
                          ]
                            ? state[
                                AssignmentField
                                  .personalTransportationFromExpectedExpenses
                              ].split(',')
                            : [];

                          if (
                            !currentExpenses.includes(
                              expectedExpense.toString()
                            )
                          ) {
                            const newExpenses = [
                              ...currentExpenses,
                              expectedExpense.toString(),
                            ].filter(Boolean);
                            onChange(
                              assignmentId,
                              AssignmentField.personalTransportationFromExpectedExpenses,
                              newExpenses.join(',')
                            );
                          }
                        }
                      }}
                      small
                      value={
                        state[
                          AssignmentField
                            .personalTransportationFromAssignmentAssignmentTypeID
                        ]
                      }
                    >
                      {assignmentTypeOptions.map((assignmentType) => (
                        <option key={assignmentType} value={assignmentType}>
                          {assignmentTypesShort[assignmentType]}
                        </option>
                      ))}
                    </Select>
                    {Number(
                      state[
                        AssignmentField
                          .personalTransportationFromAssignmentAssignmentTypeID
                      ]
                    ) === AssignmentTypeEnum.GoByPublicTransport && (
                      <CommuteTimePicker
                        // from the end assignment address to the personal transport address
                        fromAddress={joinAddress([
                          state[AssignmentField.toAddress],
                          state[AssignmentField.toCity],
                          state[AssignmentField.toZip],
                        ])}
                        toAddress={joinAddress([
                          state[
                            AssignmentField
                              .personalTransportationFromAssignmentAddress
                          ],
                          state[
                            AssignmentField
                              .personalTransportationFromAssignmentCity
                          ],
                          state[
                            AssignmentField
                              .personalTransportationFromAssignmentZip
                          ],
                        ])}
                        estimatedStartTime={
                          state[AssignmentField.estimatedStartTime] ||
                          state[AssignmentField.bookedTo] // default to estimated start time of main assignment, fallback to bookedTo if not set
                        }
                        estimatedDuration={
                          state[AssignmentField.estimatedDuration]
                        }
                        personalTransportFromMain
                        onSelectOption={(option, fromAddress, toAddress) =>
                          handleCommuteSelect(
                            option,
                            false,
                            fromAddress,
                            toAddress
                          )
                        }
                      />
                    )}
                  </div>
                </LabelWrap>
                <HorizontalWithGaps>
                  <LabelWrap
                    errorLabel={createErrorLabel(
                      AssignmentField.personalTransportationFromAssignmentEstimatedStartTime
                    )}
                    label={<strong>Estimerad starttid</strong>}
                  >
                    <Horizontal style={{ alignItems: 'center' }}>
                      <Input
                        error={
                          !!validation?.[
                            AssignmentField
                              .personalTransportationFromAssignmentEstimatedStartTime
                          ]?.error
                        }
                        warning={
                          !!validation?.[
                            AssignmentField
                              .personalTransportationFromAssignmentEstimatedStartTime
                          ]?.warning
                        }
                        min={state[AssignmentField.estimatedStartTime] || ''}
                        onChange={(eve) => {
                          const personalTransportationFromAssignmentEstimatedStartTime =
                            eve.currentTarget.value;

                          // Always commit the value, even if it (for now) starts before
                          // the main assignment's estimated end time. The validator for
                          // this field shows an inline error and disables save instead.
                          onChange(
                            assignmentId,
                            AssignmentField.personalTransportationFromAssignmentEstimatedStartTime,
                            personalTransportationFromAssignmentEstimatedStartTime
                          );
                        }}
                        small
                        type="datetime-local"
                        value={
                          state[
                            AssignmentField
                              .personalTransportationFromAssignmentEstimatedStartTime
                          ]
                        }
                      />
                    </Horizontal>
                  </LabelWrap>
                  <LabelWrap label={<strong>Estimerad tid</strong>}>
                    <HorizontalWithGaps style={{ alignItems: 'center' }}>
                      <BasicEstimatedDurationPicker
                        disabled={
                          state[
                            AssignmentField
                              .personalTransportationFromAssignmentFixedAssignmentTimeInHours
                          ] !== ''
                        }
                        caseType={currentCaseType}
                        assignmentType={
                          Number(
                            state[
                              AssignmentField
                                .personalTransportationFromAssignmentAssignmentTypeID
                            ]
                          ) as AssignmentTypeEnum
                        }
                        addresses={personalTransportToAssignmentStart}
                        value={
                          state[
                            AssignmentField
                              .personalTransportationFromAssignmentEstimatedDuration
                          ]
                            ? Number(
                                state[
                                  AssignmentField
                                    .personalTransportationFromAssignmentEstimatedDuration
                                ]
                              )
                            : undefined
                        }
                        hideMapsIcon
                        onChange={(value) => {
                          // first, update the state with the new value
                          onChange(
                            assignmentId,
                            AssignmentField.personalTransportationFromAssignmentEstimatedDuration,
                            value.toString()
                          );
                        }}
                      />
                      {state[
                        AssignmentField
                          .personalTransportationFromAssignmentFixedAssignmentTimeInHours
                      ] !== '' && (
                        <WarningDiv style={{ marginLeft: 5 }}>
                          Fast tid:{' '}
                          {
                            state[
                              AssignmentField
                                .personalTransportationFromAssignmentFixedAssignmentTimeInHours
                            ]
                          }{' '}
                          h
                        </WarningDiv>
                      )}
                      {personalTransportToAssignmentStartTime && (
                        <PaddedSpan style={{ opacity: 0.4 }}>
                          Beräknad sluttid:
                          {formatDateTime(
                            personalTransportToAssignmentStartTime
                          )}
                        </PaddedSpan>
                      )}
                    </HorizontalWithGaps>
                  </LabelWrap>
                </HorizontalWithGaps>

                <Horizontal>
                  <LabelWrap label={<strong>Förväntade utgifter</strong>}>
                    <MultiSelect
                      onChange={(v) => {
                        onChange(
                          assignmentId,
                          AssignmentField.personalTransportationFromExpectedExpenses,
                          Array.from(v).join(',')
                        );
                      }}
                      options={expenseTypeOptions}
                      small
                      value={
                        new Set(
                          state[
                            AssignmentField
                              .personalTransportationFromExpectedExpenses
                          ] === ''
                            ? []
                            : state[
                                AssignmentField
                                  .personalTransportationFromExpectedExpenses
                              ].split(',')
                        )
                      }
                    />
                  </LabelWrap>
                </Horizontal>
                <LabelWrap
                  label={<strong>Fälttestaren bokar egen biljett</strong>}
                >
                  <Checkbox
                    checked={
                      state[
                        AssignmentField
                          .personalTransportationFromAssigneeShouldBookTheirOwnTicket
                      ] === 'true'
                    }
                    onChange={(eve) =>
                      onChange(
                        assignmentId,
                        AssignmentField.personalTransportationFromAssigneeShouldBookTheirOwnTicket,
                        eve.currentTarget.checked ? 'true' : 'false'
                      )
                    }
                  />
                </LabelWrap>
                <LabelWrap label={<strong>Biljett-info</strong>}>
                  <ExtraInfoInput
                    onChange={(eve) =>
                      onChange(
                        assignmentId,
                        AssignmentField.personalTransportationFromTicketInfo,
                        eve.currentTarget.value
                      )
                    }
                    value={
                      state[
                        AssignmentField.personalTransportationFromTicketInfo
                      ]
                    }
                  />
                </LabelWrap>
              </InFormContainer>
            ),
          }
        : {
            key: 'Lägg till',
            id: fromTransportRowId,
            exclude: () => !hasAccessToInEditStopOvers,
            value: (
              <TextButton onClick={handleAddPersonalTransportFromAssignment}>
                <FontAwesomeIcon icon={faPlus} /> Personlig transport från
                uppdraget
              </TextButton>
            ),
          };

    return {
      toAssignment,
      fromAssignment,
    };
  }, [
    assignmentId,
    assignmentTypeOptions,
    assignmentTypesShort,
    createErrorLabel,
    currentCaseType,
    expenseTypeOptions,
    handleAddPersonalTransportFromAssignment,
    handleAddPersonalTransportToAssignment,
    handleCommuteSelect,
    hasAccessToInEditStopOvers,
    joinAddress,
    mkAddressInputFn,
    onChange,
    personalTransportToAssignmentEndTime,
    personalTransportToAssignmentStart,
    personalTransportToAssignmentStartTime,
    state,
    validation,
  ]);

  return result;
};

export default useAddPersonalTransportAssignmentRow;
